/**
 * ToastNotifications — floating notification system for ToolOS.
 *
 * Surfaces policy decisions, tool results, and system events as
 * non-intrusive toasts anchored to the bottom-right of the desktop.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import "../../styles/toast.css";

/* ── Data shape ── */

export interface Toast {
  id: string;
  type: "info" | "success" | "warning" | "error" | "policy";
  title: string;
  message?: string;
  duration?: number; // ms, default 5000
  timestamp: number;
}

/* ── Global imperative API ── */

type ToastListener = (toast: Toast) => void;

const listeners = new Set<ToastListener>();

let toastSeq = 0;

/** Fire-and-forget: show a toast from anywhere in the app. */
export function showToast(
  opts: Omit<Toast, "id" | "timestamp"> & { id?: string },
): void {
  const toast: Toast = {
    id: opts.id ?? `toast-${++toastSeq}-${Date.now()}`,
    type: opts.type,
    title: opts.title,
    message: opts.message,
    duration: opts.duration,
    timestamp: Date.now(),
  };
  listeners.forEach((fn) => fn(toast));
}

/* ── React hook ── */

export function useToast() {
  const add = useCallback(
    (opts: Omit<Toast, "id" | "timestamp"> & { id?: string }) => {
      showToast(opts);
    },
    [],
  );
  return { showToast: add };
}

/* ── Icons ── */

function IconClose() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M3 3l6 6M9 3l-6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TypeIcon({ type }: { type: Toast["type"] }) {
  const size = 16;
  switch (type) {
    case "success":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="var(--ot-green)" strokeWidth="1.5" />
          <path d="M5 8l2 2 4-4" stroke="var(--ot-green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "error":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="var(--ot-red)" strokeWidth="1.5" />
          <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="var(--ot-red)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "warning":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <path d="M8 2L1.5 14h13L8 2z" stroke="var(--ot-yellow)" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8 6v4" stroke="var(--ot-yellow)" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="12" r="0.8" fill="var(--ot-yellow)" />
        </svg>
      );
    case "policy":
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <path d="M8 1L2 4v4c0 3.5 2.5 6.5 6 7.5 3.5-1 6-4 6-7.5V4L8 1z" stroke="var(--ot-cyan)" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M6 8l1.5 1.5L10 6" stroke="var(--ot-cyan)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      // info
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="var(--ot-accent)" strokeWidth="1.5" />
          <path d="M8 7v4" stroke="var(--ot-accent)" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="5" r="0.8" fill="var(--ot-accent)" />
        </svg>
      );
  }
}

/* ── Component ── */

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 5000;

export function ToastNotifications() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [exiting, setExiting] = useState<Set<string>>(new Set());
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Subscribe to the global listener bus
  useEffect(() => {
    const handler: ToastListener = (toast) => {
      setToasts((prev) => {
        const next = [...prev, toast];
        // Keep only the most recent MAX_VISIBLE
        return next.slice(-MAX_VISIBLE);
      });
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  // Dismiss helper with exit animation
  const dismiss = useCallback((id: string) => {
    setExiting((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setExiting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 280); // match CSS exit animation duration
  }, []);

  // Auto-dismiss timers
  useEffect(() => {
    for (const toast of toasts) {
      if (!timers.current.has(toast.id) && !exiting.has(toast.id)) {
        const dur = toast.duration ?? DEFAULT_DURATION;
        const timer = setTimeout(() => {
          dismiss(toast.id);
          timers.current.delete(toast.id);
        }, dur);
        timers.current.set(toast.id, timer);
      }
    }
    // Clean up timers for removed toasts
    for (const [id, timer] of timers.current.entries()) {
      if (!toasts.find((t) => t.id === id)) {
        clearTimeout(timer);
        timers.current.delete(id);
      }
    }
  }, [toasts, dismiss, exiting]);

  // Clean up all timers on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  // ── SSE event listeners ──

  useEffect(() => {
    // Policy decisions are now shown in the Policy Evaluation panel.
    // We suppress per-decision toasts entirely — the panel replaces them.
    const onPolicy = () => {
      // no-op: policy feed in ChatWorkbench handles display
    };

    const onError = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message ?? detail?.error ?? "Unknown error";
      showToast({ type: "error", title: "Agent Error", message: String(msg) });
    };

    const onInject = () => {
      showToast({ type: "info", title: "UI Injected", message: "New content added to the workspace" });
    };

    window.addEventListener("opentool-agent-policy", onPolicy);
    window.addEventListener("opentool-agent-error", onError);
    window.addEventListener("opentool-inject-ui", onInject);

    return () => {
      window.removeEventListener("opentool-agent-policy", onPolicy);
      window.removeEventListener("opentool-agent-error", onError);
      window.removeEventListener("opentool-inject-ui", onInject);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="ot-toast-container" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`ot-toast ot-toast--${toast.type}${exiting.has(toast.id) ? " ot-toast--exit" : ""}`}
        >
          <div className="ot-toast__icon">
            <TypeIcon type={toast.type} />
          </div>
          <div className="ot-toast__body">
            <div className="ot-toast__title">{toast.title}</div>
            {toast.message && (
              <div className="ot-toast__message">{toast.message}</div>
            )}
          </div>
          <button
            className="ot-toast__close"
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss notification"
          >
            <IconClose />
          </button>
        </div>
      ))}
    </div>
  );
}
